/**
 * Zod schemas for every form in the web app.
 *
 * Norwegian validation messages come from the global error map in
 * [validation.ts](./validation.ts) — schemas only override when the default
 * isn't specific enough (e.g. password length, date ordering).
 *
 * Conventions:
 *   - Schema names end in `Schema`.
 *   - When the form's payload is a different shape than the field set the
 *     user fills in (e.g. trimming, casting), use `.transform()` so the
 *     output type matches what the call-site sends to the API.
 *   - Cross-field rules live in `.refine()` / `.superRefine()` so each
 *     message can target the right `path`.
 */

import { MAX_INVOICE_BYTES, MAX_INVOICE_MB } from "@fellesen/api/constants";
import { categoryInputSchema } from "@fellesen/api/schemas";
import { z } from "zod";

import { t } from "../i18n";

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const trimmedString = (max?: number) => {
  const base = z.string().trim().min(1);
  return max === undefined ? base : base.max(max);
};

const email = () => z.string().email().max(254);
const password = () => z.string().min(10, { error: t.validation.passwordTooShort });

// Year-month comparison used by settlement date validation.
const monthOf = (iso: string): string => iso.slice(0, 7);
const currentMonth = () => new Date().toISOString().slice(0, 7);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  email: email(),
  password: z.string().min(1),
});

export const registerSchema = z.object({
  email: email(),
  displayName: trimmedString(100),
  password: password(),
});

export const forgotPasswordSchema = z.object({
  email: email(),
});

export const resetPasswordSchema = z
  .object({
    password: password(),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    error: t.validation.passwordsMustMatch,
    path: ["confirm"],
  });

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export const profileSchema = z.object({
  displayName: trimmedString(100),
});

// ---------------------------------------------------------------------------
// Household
// ---------------------------------------------------------------------------

export const createHouseholdSchema = z.object({
  name: trimmedString(100),
});

export const renameHouseholdSchema = createHouseholdSchema;

export const inviteMemberSchema = z.object({
  email: email(),
});

// ---------------------------------------------------------------------------
// Category
// ---------------------------------------------------------------------------

// Re-export the API's authoritative schema so the form validates against the
// exact same rules the server enforces. Field names match the wire format
// (`matchPatterns`, not `patterns`).
export const categorySchema = categoryInputSchema;

// ---------------------------------------------------------------------------
// Settlement
// ---------------------------------------------------------------------------

const settlementDates = z
  .object({
    title: trimmedString(120),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
  })
  .superRefine((v, ctx) => {
    if (v.startDate && monthOf(v.startDate) > currentMonth()) {
      ctx.addIssue({
        code: "custom",
        path: ["startDate"],
        message: t.validation.startDateNotFutureMonth,
      });
    }
    if (v.startDate && v.endDate && v.endDate <= v.startDate) {
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: t.validation.endDateAfterStart,
      });
    }
  });

export const createSettlementSchema = settlementDates;
export const editSettlementSchema = settlementDates;

// ---------------------------------------------------------------------------
// Invoice upload
// ---------------------------------------------------------------------------

export const uploadInvoiceSchema = z.object({
  file: z
    .instanceof(File, { error: t.validation.fileRequired })
    .refine((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"), {
      error: t.validation.fileNotPdf,
    })
    .refine((f) => f.size <= MAX_INVOICE_BYTES, {
      error: t.validation.fileTooLarge(MAX_INVOICE_MB),
    }),
});

// ---------------------------------------------------------------------------
// Expense
// ---------------------------------------------------------------------------

// Negative belop er tillatt — eks. depositum-refusjon, refusjon pa faktura.
// Bare 0 avvises, ellers kan utgiften bade vere positiv (debit) eller negativ
// (kredit). Backend lagrer som NUMERIC uten constraint.
const decimalAmount = z
  .string()
  .trim()
  .min(1)
  .refine((s) => /^-?\d+([.,]\d+)?$/.test(s), { error: t.validation.invalidNumber })
  .refine((s) => Number.parseFloat(s.replace(",", ".")) !== 0, {
    error: t.validation.amountNonZero,
  });

export const expenseSchema = z.object({
  description: trimmedString(200),
  date: z.string().min(1),
  amount: decimalAmount,
  paidBy: z.string().min(1, { error: t.validation.selectMember }),
  categoryId: z.string().nullable(),
  shared: z.boolean(),
});

// ---------------------------------------------------------------------------
// Resolve payer (per-row dialog)
// ---------------------------------------------------------------------------

export const resolvePayerSchema = z.object({
  paidBy: z.string().min(1, { error: t.validation.selectMember }),
});
